import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Camera, Crosshair } from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { VideoUploadZone } from "../components/VideoUploadZone";
import { AnalysisProgress } from "../components/AnalysisProgress";
import { PerformanceReport } from "../components/PerformanceReport";
import { HistoryTracker } from "../components/HistoryTracker";
import { SessionSummary } from "../components/SessionSummary";
import { analyzeVideo, analyzeSession, listAssessments, deleteAssessment, getAssessment } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1593341646782-e0b495cff86d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwYmF0c21hbiUyMGJhdHRpbmd8ZW58MHx8fHwxNzg5Njc1MzcxfDA&ixlib=rb-4.1.0&q=85";

export default function Analyzer() {
  const [view, setView] = useState("home"); // home | analyzing | report | history
  const [uploadPct, setUploadPct] = useState(0);
  const [reportSet, setReportSet] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [batchMode, setBatchMode] = useState("single");
  const [history, setHistory] = useState([]);

  const current = reportSet[activeIdx] || null;

  const loadHistory = async () => {
    try {
      setHistory(await listAssessments());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadHistory();
    const params = new URLSearchParams(window.location.search);
    const rid = params.get("report");
    if (rid) {
      getAssessment(rid)
        .then((a) => {
          setReportSet([a]);
          setActiveIdx(0);
          setBatchMode("single");
          setView("report");
        })
        .catch(() => {
          toast.error("Assessment not found", { description: "This shared report may have been removed." });
        });
    }
  }, []);

  const handleAnalyze = async ({ files, batterName, hand, mode }) => {
    setView("analyzing");
    setUploadPct(0);
    try {
      const result =
        mode === "session"
          ? await analyzeSession({ file: files[0].file, batterName, handedness: hand }, setUploadPct)
          : await analyzeVideo({ files, batterName, handedness: hand, mode }, setUploadPct);
      const items = result.assessments || [];
      if (!items.length) throw new Error("No assessment returned");
      setReportSet(items);
      setActiveIdx(0);
      setBatchMode(result.mode || "single");
      setView("report");
      loadHistory();
      if (mode === "session") {
        toast.success(`Session split into ${items.length} shot${items.length > 1 ? "s" : ""}`, {
          description: "Review each shot using the tabs at the top.",
        });
      } else if (items.length > 1) {
        toast.success(`${items.length} shots analyzed`, { description: "Switch between reports at the top." });
      } else {
        toast.success("Assessment ready", { description: `Net Efficiency Score: ${items[0].overall_score}/100` });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Analysis failed. Try shorter, clearer clips.";
      toast.error("Could not analyze video", { description: msg });
      setView("home");
    }
  };

  const handleOpen = (a) => {
    setReportSet([a]);
    setActiveIdx(0);
    setBatchMode("single");
    setView("report");
  };

  const handleDelete = async (id) => {
    try {
      await deleteAssessment(id);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Assessment deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F12] text-slate-100">
      <AppHeader
        view={view}
        onNavigate={(v) => {
          if (v === "history") {
            loadHistory();
            setView("history");
          } else {
            setView("home");
          }
        }}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {view === "home" && (
          <>
            {/* Hero */}
            <section className="relative overflow-hidden rounded-3xl border border-slate-800 mb-10 tech-grid">
              <div className="absolute inset-0">
                <img src={HERO_IMG} alt="Cricket batsman driving at net practice" className="w-full h-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F12] via-[#0B0F12]/85 to-[#0B0F12]/40" />
              </div>
              <div className="relative px-6 sm:px-10 py-12 sm:py-16 max-w-2xl">
                <div className="inline-flex items-center gap-2 font-mono-tel text-[10px] tracking-[0.25em] uppercase text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1.5 mb-5">
                  <ScanLine className="h-3.5 w-3.5" /> AI Batting Biomechanics
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none uppercase text-slate-50">
                  Grade every <span className="text-emerald-400">net session</span> like a pro coach
                </h1>
                <p className="text-base sm:text-lg text-slate-300 mt-5 leading-relaxed">
                  Upload a short net-practice clip from any angle. Our AI vision engine breaks down stance,
                  movement, shot execution and mental focus — with scores, faults and drills.
                </p>
                <div className="flex flex-wrap gap-5 mt-8">
                  {[
                    { icon: Crosshair, label: "Stance & V-Grip" },
                    { icon: Camera, label: "Trigger & Footwork" },
                    { icon: ScanLine, label: "Shot & Focus" },
                  ].map((f) => (
                    <div key={f.label} className="flex items-center gap-2 text-sm text-slate-300">
                      <f.icon className="h-4 w-4 text-cyan-400" /> {f.label}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Upload workspace */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7">
                <div className="rounded-3xl border border-slate-800 bg-[#0e141a] p-6 sm:p-8">
                  <h2 className="font-heading text-2xl font-bold text-slate-100 mb-1">New Net Assessment</h2>
                  <p className="text-sm text-slate-400 mb-6">Provide a clip and session details to generate a report.</p>
                  <VideoUploadZone onAnalyze={handleAnalyze} disabled={false} />
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-3xl border border-slate-800 bg-[#12181F] p-6">
                  <p className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase mb-4">
                    Camera Angle Guide
                  </p>
                  <div className="flex flex-col gap-4 text-sm">
                    {[
                      ["Side-on", "Best for stance width, trigger movement, weight transfer and follow-through."],
                      ["Front-on", "Best for head position, chest alignment, grip and bat face at impact."],
                      ["Behind-bowler", "Great for judging line, footwork toward the pitch of the ball."],
                    ].map(([t, d]) => (
                      <div key={t} className="flex gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                        <div>
                          <p className="font-semibold text-slate-200">{t}</p>
                          <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{d}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-5 border-t border-slate-800 flex items-center justify-between">
                    <span className="text-sm text-slate-400">Past sessions</span>
                    <span className="font-mono-tel font-bold text-emerald-400 text-lg">{history.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {view === "analyzing" && (
          <div className="max-w-2xl mx-auto pt-6">
            <AnalysisProgress uploadPct={uploadPct} />
          </div>
        )}

        {view === "report" && current && (
          <>
            <button
              onClick={() => setView("home")}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-6 transition-colors"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="h-4 w-4" /> New assessment
            </button>
            {batchMode === "session" && reportSet.length > 1 && <SessionSummary items={reportSet} />}
            {reportSet.length > 1 && (
              <div data-testid="report-shot-switcher" className="flex flex-wrap items-center gap-2 mb-6">
                <span className="font-mono-tel text-[10px] tracking-[0.2em] text-slate-500 uppercase mr-1">Shots</span>
                {reportSet.map((a, i) => (
                  <button
                    key={a.id}
                    data-testid={`report-shot-tab-${i}`}
                    onClick={() => setActiveIdx(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                      i === activeIdx
                        ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                        : "bg-[#12181F] border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    Shot {i + 1} · {a.overall_score}
                  </button>
                ))}
              </div>
            )}
            <PerformanceReport assessment={current} />
          </>
        )}

        {view === "history" && (
          <div className="animate-fade-up">
            <h2 className="font-heading text-3xl font-bold text-slate-100 mb-1">Assessment History</h2>
            <p className="text-sm text-slate-400 mb-8">Track the batter's technique trajectory across net sessions.</p>
            <HistoryTracker items={history} onOpen={handleOpen} onDelete={handleDelete} />
          </div>
        )}
      </main>

      <footer className="border-t border-slate-800 mt-16 py-8 flex flex-col items-center gap-4">
        <div className="flex items-center gap-3" data-testid="footer-powered-by-rezzil">
          <span className="font-mono-tel text-[11px] tracking-[0.2em] text-slate-500 uppercase">Powered by</span>
          <a href="https://rezzil.com" target="_blank" rel="noopener noreferrer" className="opacity-85 hover:opacity-100 transition-opacity">
            <img src="/rezzil-logo.png" alt="Rezzil" className="h-5 w-auto" />
          </a>
        </div>
        <p className="font-mono-tel text-[11px] tracking-wider text-slate-600 uppercase">
          CreaseAI · Net Biomechanics · AI Vision
        </p>
      </footer>
    </div>
  );
}
